import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import CurrentPlanCard from './components/CurrentPlanCard';
import PlanComparison from './components/PlanComparison';
import PaymentMethods from './components/PaymentMethods';
import UsageMetrics from './components/UsageMetrics';
import BillingHistory from './components/BillingHistory';
import ReferralProgram from './components/ReferralProgram';
import CancellationOptions from './components/CancellationOptions';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { paymentService } from '../../services/paymentService';

const SubscriptionManagement = () => {
  const { user, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [orders, setOrders] = useState([]);
  const [paymentTransactions, setPaymentTransactions] = useState([]);

  useEffect(() => {
    if (user?.id) {
      loadSubscriptionData();
    }
  }, [user?.id]);

  const loadSubscriptionData = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      
      // Load all subscription-related data
      const [subscription, methods, userOrders, transactions] = await Promise.all([
        paymentService?.getUserSubscription(user?.id),
        paymentService?.getPaymentMethods(user?.id),
        paymentService?.getOrders(user?.id),
        paymentService?.getPaymentTransactions(user?.id)
      ]);

      setCurrentSubscription(subscription);
      setPaymentMethods(methods);
      setOrders(userOrders);
      setPaymentTransactions(transactions);
    } catch (error) {
      console.error('Error loading subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Transform data for existing components
  const getCurrentPlan = () => {
    if (!currentSubscription) return null;

    return {
      type: currentSubscription?.subscription_plans?.name?.toLowerCase() || 'basic',
      name: currentSubscription?.subscription_plans?.name || 'Basic Plan',
      status: currentSubscription?.status || 'active',
      billingCycle: currentSubscription?.subscription_plans?.billing_interval || 'monthly',
      price: currentSubscription?.subscription_plans?.amount || 0,
      nextPayment: currentSubscription?.current_period_end || new Date()?.toISOString(),
      features: currentSubscription?.subscription_plans?.features || {}
    };
  };

  const getBillingHistory = () => {
    return orders?.map(order => ({
      id: order?.id,
      date: order?.created_at,
      description: order?.order_items?.[0]?.name || 'Subscription',
      plan: order?.order_items?.[0]?.subscription_plans?.name || 'Unknown',
      amount: order?.total_amount || 0,
      status: order?.status === 'completed' ? 'paid' : order?.status,
      invoiceNumber: order?.order_number,
      paymentMethod: { brand: 'Card', last4: '****' } // You might want to join with payment_methods
    })) || [];
  };

  const getMockUsage = () => ({
    // Mock usage data - in a real app, you'd fetch this from your API
    apiCalls: Math.floor(Math.random() * 8000) + 1000,
    predictions: Math.floor(Math.random() * 20) + 5,
    signals: Math.floor(Math.random() * 15) + 3,
    portfolios: Math.floor(Math.random() * 3) + 1,
    storage: Math.round((Math.random() * 2 + 0.5) * 10) / 10,
    supportTickets: Math.floor(Math.random() * 3)
  });

  const getLimits = () => {
    const plan = getCurrentPlan();
    if (!plan) return {};
    
    // Default limits based on plan type
    const limitsMap = {
      basic: {
        apiCalls: 5000,
        predictions: 10,
        signals: 5,
        portfolios: 1,
        storage: 1,
        supportTickets: 2
      },
      professional: {
        apiCalls: 15000,
        predictions: 50,
        signals: 25,
        portfolios: 5,
        storage: 10,
        supportTickets: 10
      },
      premium: {
        apiCalls: 'unlimited',
        predictions: 'unlimited',
        signals: 'unlimited',
        portfolios: 'unlimited',
        storage: 50,
        supportTickets: 'unlimited'
      }
    };

    return limitsMap?.[plan?.type] || limitsMap?.basic;
  };

  // Mock referral data
  const getReferralData = () => ({
    referralCode: user?.id?.slice(0, 8)?.toUpperCase() || 'USER2025',
    totalReferrals: Math.floor(Math.random() * 15) + 5,
    totalEarned: Math.floor(Math.random() * 500) + 100,
    pendingEarnings: Math.floor(Math.random() * 100) + 20,
    monthlyReferrals: Math.floor(Math.random() * 5) + 1,
    rewardPerReferral: 30,
    recentReferrals: []
  });

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'plans', label: 'Plans', icon: 'Package' },
    { id: 'billing', label: 'Billing', icon: 'CreditCard' },
    { id: 'usage', label: 'Usage', icon: 'BarChart3' },
    { id: 'referrals', label: 'Referrals', icon: 'Users' },
    { id: 'settings', label: 'Settings', icon: 'Settings' }
  ];

  const handleLogin = () => {
    window.location.href = '/user-login';
  };

  const handleLogout = () => {
    window.location.href = '/user-login';
  };

  const handleUpgrade = () => {
    window.location.href = '/checkout';
  };

  const handleManageBilling = () => {
    setActiveTab('billing');
  };

  const handleSelectPlan = (planId, billingCycle) => {
    console.log(`Selected plan: ${planId} with ${billingCycle} billing`);
    window.location.href = '/checkout';
  };

  const handleAddPayment = () => {
    console.log('Add payment method clicked');
  };

  const handleRemovePayment = (paymentId) => {
    console.log('Remove payment method:', paymentId);
  };

  const handleSetDefault = (paymentId) => {
    console.log('Set default payment method:', paymentId);
  };

  const handleDownloadInvoice = (transactionId) => {
    console.log(`Downloading invoice for transaction: ${transactionId}`);
  };

  const handleShareReferral = (platform) => {
    console.log(`Shared referral on: ${platform}`);
  };

  const handleCancel = (cancellationData) => {
    console.log('Cancellation requested:', cancellationData);
  };

  const handlePause = () => {
    console.log('Subscription paused');
  };

  const handleDowngrade = (planType) => {
    console.log(`Downgrading to: ${planType}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} onLogin={handleLogin} onLogout={handleLogout} onUpgrade={handleUpgrade} />
        <div className="pt-16">
          <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading subscription details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    const currentPlan = getCurrentPlan();
    const billingHistory = getBillingHistory();
    const usage = getMockUsage();
    const limits = getLimits();
    const referralData = getReferralData();

    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <CurrentPlanCard
              currentPlan={currentPlan}
              onUpgrade={handleUpgrade}
              onManageBilling={handleManageBilling}
            />
            <UsageMetrics usage={usage} limits={limits} />
          </div>
        );
      case 'plans':
        return (
          <PlanComparison
            currentPlan={currentPlan}
            onSelectPlan={handleSelectPlan}
          />
        );
      case 'billing':
        return (
          <div className="space-y-6">
            <PaymentMethods
              paymentMethods={paymentMethods}
              onAddPayment={handleAddPayment}
              onRemovePayment={handleRemovePayment}
              onSetDefault={handleSetDefault}
            />
            <BillingHistory
              billingHistory={billingHistory}
              onDownloadInvoice={handleDownloadInvoice}
            />
          </div>
        );
      case 'usage':
        return (
          <UsageMetrics usage={usage} limits={limits} />
        );
      case 'referrals':
        return (
          <ReferralProgram
            referralData={referralData}
            onShareReferral={handleShareReferral}
          />
        );
      case 'settings':
        return (
          <CancellationOptions
            currentPlan={currentPlan}
            onCancel={handleCancel}
            onPause={handlePause}
            onDowngrade={handleDowngrade}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} onLogin={handleLogin} onLogout={handleLogout} onUpgrade={handleUpgrade} />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="CreditCard" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Subscription Management</h1>
                <p className="text-muted-foreground">Manage your plan, billing, and account preferences</p>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="mb-8">
            <div className="border-b border-border">
              <nav className="flex space-x-8 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-standard ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="mb-8">
            {renderTabContent()}
          </div>

          {/* Quick Actions */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
            <h2 className="text-lg font-semibold text-card-foreground mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Button
                variant="outline"
                onClick={() => window.location.href = '/trading-dashboard'}
                iconName="BarChart3"
                iconPosition="left"
                fullWidth
              >
                Dashboard
              </Button>
              <Button
                variant="outline"
                onClick={() => window.location.href = '/ai-predictions-detail'}
                iconName="TrendingUp"
                iconPosition="left"
                fullWidth
              >
                Predictions
              </Button>
              <Button
                variant="outline"
                onClick={() => window.location.href = '/portfolio-tracker'}
                iconName="PieChart"
                iconPosition="left"
                fullWidth
              >
                Portfolio
              </Button>
              <Button
                variant="outline"
                onClick={() => window.location.href = '/trading-signals'}
                iconName="Zap"
                iconPosition="left"
                fullWidth
              >
                Signals
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Footer */}
      <footer className="bg-card border-t border-border mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              © {new Date()?.getFullYear()} CryptoPredict Pro. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SubscriptionManagement;